import { useState, useEffect, useCallback, useRef } from 'react';

// iOS Safari uses window.orientation (deprecated but still works)
// We access it via type assertion instead of extending Window to avoid conflicts

interface DeviceOrientationState {
  heading: number | null;
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
  error: string | null;
  isSupported: boolean;
  isActive: boolean;
  needsPermission: boolean; // iOS 13+ requires explicit permission
  permissionGranted: boolean | null; // null = not requested yet
}

interface UseDeviceOrientationOptions {
  smoothingFactor?: number; // 0-1, higher = more smoothing
}

const defaultOptions: UseDeviceOrientationOptions = {
  smoothingFactor: 0.3,
};

/**
 * Check if DeviceOrientation requires permission prompt (iOS 13+)
 */
export function requiresOrientationPermission(): boolean {
  return (
    typeof DeviceOrientationEvent !== 'undefined' &&
    typeof (DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission === 'function'
  );
}

// iOS requires permission request
async function requestPermission(): Promise<boolean> {
  if (
    typeof DeviceOrientationEvent !== 'undefined' &&
    typeof (DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission === 'function'
  ) {
    try {
      const permission = await (DeviceOrientationEvent as unknown as { requestPermission: () => Promise<string> }).requestPermission();
      return permission === 'granted';
    } catch {
      return false;
    }
  }
  // Non-iOS devices don't need permission
  return true;
}

/**
 * Normalize angle to 0-360 range
 */
function normalizeAngle(angle: number): number {
  return ((angle % 360) + 360) % 360;
}

/**
 * Calculate compass heading from device orientation
 * Handles both iOS (webkitCompassHeading) and Android (alpha)
 */
function getCompassHeading(event: DeviceOrientationEvent): number | null {
  // iOS provides webkitCompassHeading directly (degrees from magnetic north)
  // This is the most reliable on iOS devices
  if ('webkitCompassHeading' in event && typeof event.webkitCompassHeading === 'number') {
    return event.webkitCompassHeading;
  }

  // Android needs calculation from alpha
  // alpha is the compass direction the device is facing
  if (event.alpha !== null) {
    // On Android, alpha is the rotation around Z axis
    // 0 = North, 90 = East, 180 = South, 270 = West
    // We need to compensate for screen orientation
    // Note: window.screen.orientation is NOT available on iOS Safari
    let screenOrientation = 0;
    if (window.screen?.orientation?.angle !== undefined) {
      screenOrientation = window.screen.orientation.angle;
    } else if (typeof (window as unknown as { orientation?: number }).orientation === 'number') {
      // Fallback for older browsers (deprecated but works on iOS)
      screenOrientation = (window as unknown as { orientation: number }).orientation;
    }
    return normalizeAngle(360 - event.alpha + screenOrientation);
  }

  return null;
}

/**
 * Custom hook for device orientation/compass heading
 */
export function useDeviceOrientation(options: UseDeviceOrientationOptions = {}) {
  const opts = { ...defaultOptions, ...options };
  
  const [state, setState] = useState<DeviceOrientationState>({
    heading: null,
    alpha: null,
    beta: null,
    gamma: null,
    error: null,
    isSupported: typeof DeviceOrientationEvent !== 'undefined',
    isActive: false,
    needsPermission: requiresOrientationPermission(),
    permissionGranted: null,
  });

  const smoothedHeadingRef = useRef<number | null>(null);
  const permissionGrantedRef = useRef<boolean | null>(null);

  /**
   * Smooth the heading using exponential smoothing to reduce jitter
   */
  const smoothHeading = useCallback((newHeading: number): number => {
    if (smoothedHeadingRef.current === null) {
      smoothedHeadingRef.current = newHeading;
      return newHeading;
    }

    const prev = smoothedHeadingRef.current;
    const diff = newHeading - prev;

    // Handle wrap-around at 0/360
    let adjustedDiff = diff;
    if (Math.abs(diff) > 180) {
      adjustedDiff = diff > 0 ? diff - 360 : diff + 360;
    }

    const smoothed = normalizeAngle(prev + adjustedDiff * (1 - opts.smoothingFactor!));
    smoothedHeadingRef.current = smoothed;
    return smoothed;
  }, [opts.smoothingFactor]);

  const handleOrientation = useCallback((event: DeviceOrientationEvent) => {
    const rawHeading = getCompassHeading(event);
    const heading = rawHeading !== null ? smoothHeading(rawHeading) : null;

    setState((prev) => ({
      ...prev,
      heading,
      alpha: event.alpha ?? null,
      beta: event.beta ?? null,
      gamma: event.gamma ?? null,
      error: null,
    }));
  }, [smoothHeading]);

  const startListening = useCallback(async () => {
    if (!state.isSupported) {
      setState((prev) => ({
        ...prev,
        error: 'Device orientation is not supported by your browser.',
      }));
      return false;
    }

    // Request permission if needed (iOS)
    if (permissionGrantedRef.current === null) {
      const granted = await requestPermission();
      permissionGrantedRef.current = granted;
      
      // Update state with permission result
      setState((prev) => ({
        ...prev,
        permissionGranted: granted,
      }));
      
      if (!granted) {
        setState((prev) => ({
          ...prev,
          error: 'Compass permission denied. Please enable Motion & Orientation in Settings > Safari.',
          permissionGranted: false,
        }));
        return false;
      }
    }

    if (!permissionGrantedRef.current) {
      setState((prev) => ({
        ...prev,
        error: 'Compass permission denied. Please enable Motion & Orientation in Settings > Safari.',
        permissionGranted: false,
      }));
      return false;
    }

    window.addEventListener('deviceorientation', handleOrientation, true);
    setState((prev) => ({ ...prev, isActive: true, error: null }));
    return true;
  }, [state.isSupported, handleOrientation]);

  const stopListening = useCallback(() => {
    window.removeEventListener('deviceorientation', handleOrientation, true);
    setState((prev) => ({ ...prev, isActive: false }));
    smoothedHeadingRef.current = null;
  }, [handleOrientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, [handleOrientation]);

  return {
    ...state,
    startListening,
    stopListening,
    requestPermission: async () => {
      const granted = await requestPermission();
      permissionGrantedRef.current = granted;
      return granted;
    },
  };
}
